/**
 * DocumentList Component
 *
 * Displays a list of processed documents from ContentProcessorAPI.
 * Shows file name, status, scores, and processing time.
 * Provides actions for download, view details, and delete.
 *
 * Requirements: 2.1, 2.5
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  ArrowDownload20Regular,
  Delete20Regular,
  Document24Regular,
  DocumentPdf24Regular,
  DocumentText24Regular,
  MoreHorizontal20Regular,
  Folder24Regular,
  Checkmark20Regular,
  ErrorCircle20Regular,
  Clock20Regular,
  Eye20Regular,
  Info16Regular,
} from '@fluentui/react-icons';
import type { ProcessedDocument } from '../../types/documents';
import styles from './DocumentList.module.css';

// ============================================================================
// Constants
// ============================================================================

/** Status display configuration */
const STATUS_CONFIG: Record<string, { label: string; className: string; isActive: boolean }> = {
  completed: { label: 'Completed', className: 'statusCompleted', isActive: false },
  processing: { label: 'Processing', className: 'statusProcessing', isActive: true },
  extract: { label: 'Extracting', className: 'statusProcessing', isActive: true },
  map: { label: 'Mapping', className: 'statusProcessing', isActive: true },
  evaluate: { label: 'Evaluating', className: 'statusProcessing', isActive: true },
  save: { label: 'Saving', className: 'statusProcessing', isActive: true },
  pending: { label: 'Pending', className: 'statusPending', isActive: false },
  failed: { label: 'Error', className: 'statusError', isActive: false },
};

// ============================================================================
// Types
// ============================================================================

export interface DocumentListProps {
  /** Array of processed documents to display */
  documents: ProcessedDocument[];
  /** Callback when a document is selected for viewing */
  onSelect: (document: ProcessedDocument) => void;
  /** Callback when delete action is triggered */
  onDelete: (processId: string) => void;
  /** Callback when download action is triggered */
  onDownload: (processId: string) => void;
  /** Callback when view details action is triggered (optional) */
  onViewDetails?: (processId: string) => void;
  /** Whether the list is in a loading state */
  isLoading?: boolean;
  /** Set of selected document IDs for comparison */
  selectedIds?: Set<string>;
  /** Callback when a document checkbox is toggled */
  onToggleSelect?: (processId: string) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the appropriate icon for a document based on its MIME type
 */
function getDocumentIcon(mimeType: string | null): React.ReactNode {
  if (mimeType === 'application/pdf') {
    return <DocumentPdf24Regular className={styles.documentIcon} />;
  }
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword'
  ) {
    return <DocumentText24Regular className={styles.documentIcon} />;
  }
  return <Document24Regular className={styles.documentIcon} />;
}

/**
 * Gets the status icon based on processing status.
 * Active processing stages get a spinning indicator.
 */
function getStatusIcon(status: string): React.ReactNode {
  switch (status) {
    case 'completed':
      return <Checkmark20Regular className={styles.statusIconCompleted} />;
    case 'failed':
      return <ErrorCircle20Regular className={styles.statusIconError} />;
    case 'processing':
    case 'extract':
    case 'map':
    case 'evaluate':
    case 'save':
      return <span className={styles.spinner} role="status" aria-label="Processing" />;
    default:
      return <Clock20Regular className={styles.statusIconPending} />;
  }
}

/**
 * Formats a date string for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Formats a datetime string for display with time
 */
function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats processing time (e.g., "00:04:03.802" -> "4m 3s")
 */
function formatProcessingTime(timeString: string | null): string {
  if (!timeString) return '-';

  // Parse HH:MM:SS.mmm format
  const match = timeString.match(/^(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return timeString;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
}

/**
 * Formats a score as a percentage
 */
function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

/**
 * Returns a CSS color for a score bar based on the value (0-1).
 * Red at low scores, amber in the middle, green at high scores.
 */
function getScoreColor(score: number): string {
  if (score >= 0.8) return '#107c10';
  if (score >= 0.6) return '#986f0b';
  return '#c4314b';
}

// ============================================================================
// Sub-Components
// ============================================================================

interface DocumentRowProps {
  document: ProcessedDocument;
  onSelect: (document: ProcessedDocument) => void;
  onDelete: (processId: string) => void;
  onDownload: (processId: string) => void;
  onViewDetails?: (processId: string) => void;
  isChecked?: boolean;
  onToggleSelect?: (processId: string) => void;
}

/**
 * Individual document row component
 */
const DocumentRow: React.FC<DocumentRowProps> = ({
  document,
  onSelect,
  onDelete,
  onDownload,
  onViewDetails,
  isChecked = false,
  onToggleSelect,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [menuAbove, setMenuAbove] = useState(false);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  const handleRowClick = useCallback(() => {
    onSelect(document);
  }, [document, onSelect]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(document);
      }
    },
    [document, onSelect]
  );

  const handleDownload = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onDownload(document.processId);
      setShowActions(false);
    },
    [document.processId, onDownload]
  );

  const handleViewDetails = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (onViewDetails) {
        onViewDetails(document.processId);
      } else {
        onSelect(document);
      }
      setShowActions(false);
    },
    [document, onSelect, onViewDetails]
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onDelete(document.processId);
      setShowActions(false);
    },
    [document.processId, onDelete]
  );

  const handleActionsToggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowActions((prev) => {
      if (!prev && actionsButtonRef.current) {
        const rect = actionsButtonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        // Menu is roughly 130px tall (3 items); flip if not enough room
        setMenuAbove(spaceBelow < 150);
      }
      return !prev;
    });
  }, []);

  const handleActionsKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        setShowActions((prev) => !prev);
      } else if (event.key === 'Escape') {
        setShowActions(false);
      }
    },
    []
  );

  // Close actions menu when clicking outside
  const handleActionsBlur = useCallback(
    (event: React.FocusEvent) => {
      // Check if the new focus target is within the actions menu
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (!relatedTarget?.closest(`.${styles.actionsMenu}`)) {
        setShowActions(false);
      }
    },
    []
  );

  const statusConfig = STATUS_CONFIG[document.status] || STATUS_CONFIG.pending;

  const handleCheckboxChange = useCallback(
    (event: React.MouseEvent | React.ChangeEvent) => {
      event.stopPropagation();
      onToggleSelect?.(document.processId);
    },
    [document.processId, onToggleSelect]
  );

  return (
    <tr
      className={`${styles.documentRow} ${isChecked ? styles.documentRowSelected : ''}`}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      role="row"
      aria-label={`Document: ${document.processedFileName}`}
    >
      {/* Selection Checkbox */}
      {onToggleSelect && (
        <td className={styles.checkboxCell} onClick={handleCheckboxChange}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={isChecked}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${document.processedFileName} for comparison`}
            disabled={document.status !== 'completed'}
          />
        </td>
      )}

      {/* Document Name */}
      <td className={styles.nameCell}>
        <div className={styles.nameContent}>
          {getDocumentIcon(document.processedFileMimeType)}
          <div className={styles.nameDetails}>
            <span className={styles.documentName}>
              {document.processedFileName || 'Unnamed Document'}
            </span>
            <span className={styles.documentMeta}>
              Imported {formatDate(document.importedTime)}
            </span>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className={styles.statusCell}>
        <div className={styles.statusBadge}>
          {getStatusIcon(document.status)}
          <span className={styles[statusConfig.className]}>
            {statusConfig.label}
          </span>
        </div>
      </td>

      {/* Coverage */}
      <td className={styles.scoreCell}>
        {document.status === 'completed' ? (
          <div className={styles.scoreContainer}>
            <div
              className={styles.scoreBar}
              style={{ width: `${document.schemaScore * 100}%`, background: getScoreColor(document.schemaScore) }}
            />
            <span className={styles.scoreValue} style={{ color: getScoreColor(document.schemaScore) }}>
              {formatScore(document.schemaScore)}
            </span>
          </div>
        ) : document.status === 'failed' ? (
          <span className={styles.pendingScore}>-</span>
        ) : (
          <span className={styles.pendingScore}>Pending</span>
        )}
      </td>

      {/* Confidence */}
      <td className={styles.scoreCell}>
        {document.status === 'completed' ? (
          <div className={styles.scoreContainer}>
            <div
              className={styles.scoreBar}
              style={{ width: `${document.entityScore * 100}%`, background: getScoreColor(document.entityScore) }}
            />
            <span className={styles.scoreValue} style={{ color: getScoreColor(document.entityScore) }}>
              {formatScore(document.entityScore)}
            </span>
          </div>
        ) : document.status === 'failed' ? (
          <span className={styles.pendingScore}>-</span>
        ) : (
          <span className={styles.pendingScore}>Pending</span>
        )}
      </td>

      {/* Processing Time */}
      <td className={styles.timeCell}>
        <span className={styles.processingTime}>
          {formatProcessingTime(document.processedTime)}
        </span>
      </td>

      {/* Actions */}
      <td className={styles.actionsCell}>
        <div className={styles.actionsContainer} onBlur={handleActionsBlur}>
          <button
            type="button"
            ref={actionsButtonRef}
            className={styles.actionsButton}
            onClick={handleActionsToggle}
            onKeyDown={handleActionsKeyDown}
            aria-label={`Actions for ${document.processedFileName}`}
            aria-expanded={showActions}
            aria-haspopup="menu"
          >
            <MoreHorizontal20Regular />
          </button>

          {showActions && (
            <div className={`${styles.actionsMenu} ${menuAbove ? styles.actionsMenuAbove : ''}`} role="menu">
              <button
                type="button"
                className={styles.actionItem}
                onClick={handleViewDetails}
                role="menuitem"
              >
                <Eye20Regular className={styles.actionIcon} />
                <span>View Details</span>
              </button>
              <button
                type="button"
                className={styles.actionItem}
                onClick={handleDownload}
                role="menuitem"
                disabled={document.status !== 'completed'}
              >
                <ArrowDownload20Regular className={styles.actionIcon} />
                <span>Download</span>
              </button>
              <button
                type="button"
                className={`${styles.actionItem} ${styles.deleteAction}`}
                onClick={handleDelete}
                role="menuitem"
              >
                <Delete20Regular className={styles.actionIcon} />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ============================================================================
// Loading Skeleton
// ============================================================================

const LoadingSkeleton: React.FC = () => (
  <tbody>
    {[1, 2, 3].map((i) => (
      <tr key={i} className={styles.skeletonRow}>
        <td className={styles.nameCell}>
          <div className={styles.nameContent}>
            <div className={`${styles.skeleton} ${styles.skeletonIcon}`} />
            <div className={styles.nameDetails}>
              <div className={`${styles.skeleton} ${styles.skeletonName}`} />
              <div className={`${styles.skeleton} ${styles.skeletonMeta}`} />
            </div>
          </div>
        </td>
        <td className={styles.statusCell}>
          <div className={`${styles.skeleton} ${styles.skeletonStatus}`} />
        </td>
        <td className={styles.scoreCell}>
          <div className={`${styles.skeleton} ${styles.skeletonScore}`} />
        </td>
        <td className={styles.scoreCell}>
          <div className={`${styles.skeleton} ${styles.skeletonScore}`} />
        </td>
        <td className={styles.timeCell}>
          <div className={`${styles.skeleton} ${styles.skeletonTime}`} />
        </td>
        <td className={styles.actionsCell}>
          <div className={`${styles.skeleton} ${styles.skeletonActions}`} />
        </td>
      </tr>
    ))}
  </tbody>
);

// ============================================================================
// Empty State
// ============================================================================

const EmptyState: React.FC = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <Folder24Regular />
    </div>
    <h3 className={styles.emptyTitle}>No documents uploaded</h3>
    <p className={styles.emptyDescription}>
      Upload your operational documents to get started with SORA evidence extraction.
    </p>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * DocumentList displays a table of processed documents with actions.
 *
 * Features:
 * - Displays document name, status, coverage, confidence, and processing time
 * - Provides view details, download, and delete actions
 * - Shows loading skeleton while data is being fetched
 * - Shows empty state when no documents are available
 * - Supports keyboard navigation and accessibility
 *
 * @example
 * // Basic usage
 * <DocumentList
 *   documents={documents}
 *   onSelect={(doc) => console.log('Selected:', doc)}
 *   onDelete={(id) => handleDelete(id)}
 *   onDownload={(id) => handleDownload(id)}
 * />
 *
 * @example
 * // With loading state
 * <DocumentList
 *   documents={[]}
 *   onSelect={handleSelect}
 *   onDelete={handleDelete}
 *   onDownload={handleDownload}
 *   isLoading={true}
 * />
 */
export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  onSelect,
  onDelete,
  onDownload,
  onViewDetails,
  isLoading = false,
  selectedIds,
  onToggleSelect,
}) => {
  // Show empty state if not loading and no documents
  if (!isLoading && documents.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className={styles.documentList}>
      <table className={styles.table} role="grid" aria-label="Processed documents list">
        <thead className={styles.tableHeader}>
          <tr>
            {onToggleSelect && (
              <th scope="col" className={`${styles.headerCell} ${styles.checkboxHeader}`}>
                <span className={styles.srOnly}>Select</span>
              </th>
            )}
            <th scope="col" className={styles.headerCell}>
              Document Name
            </th>
            <th scope="col" className={styles.headerCell}>
              Status
            </th>
            <th scope="col" className={styles.headerCell}>
              <span className={styles.headerWithInfo}>
                Coverage
                <span className={styles.infoTooltipWrapper}>
                  <Info16Regular className={styles.infoIcon} aria-hidden="true" />
                  <span className={styles.infoTooltip} role="tooltip">
                    Coverage measures how many of the expected schema fields were found in the document. A higher score means more fields were successfully extracted.
                  </span>
                </span>
              </span>
            </th>
            <th scope="col" className={styles.headerCell}>
              <span className={styles.headerWithInfo}>
                Confidence
                <span className={styles.infoTooltipWrapper}>
                  <Info16Regular className={styles.infoIcon} aria-hidden="true" />
                  <span className={styles.infoTooltip} role="tooltip">
                    Confidence reflects how certain the AI model is about the accuracy of the extracted values. A higher score indicates greater reliability of the results.
                  </span>
                </span>
              </span>
            </th>
            <th scope="col" className={styles.headerCell}>
              Processing Time
            </th>
            <th scope="col" className={`${styles.headerCell} ${styles.actionsHeader}`}>
              <span className={styles.srOnly}>Actions</span>
            </th>
          </tr>
        </thead>

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <tbody>
            {documents.map((document) => (
              <DocumentRow
                key={document.processId}
                document={document}
                onSelect={onSelect}
                onDelete={onDelete}
                onDownload={onDownload}
                onViewDetails={onViewDetails}
                isChecked={selectedIds?.has(document.processId) ?? false}
                onToggleSelect={onToggleSelect}
              />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default DocumentList;
